import React, { useState } from 'react';
import { RefreshCw, Wrench } from 'lucide-react';
import { Button } from './Button';
import { SiteFooter } from './SiteFooter';
import { useMaintenanceCountdown } from '../hooks/useMaintenanceCountdown';
import { useMaintenanceTelemetry } from '../hooks/useMaintenanceTelemetry';
import { formatTime, localTimeZone, UTC_ZONE } from '../utils/time';
import { maintenance } from '../data/maintenance';
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor(total % 3600 / 60);
  const seconds = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
export function ScheduledMaintenance() {
  const {
    remaining,
    endsAt,
    isOverdue
  } = useMaintenanceCountdown(maintenance.endsAtIso);
  const [isRetrying, setIsRetrying] = useState(false);
  const {
    trackRetry
  } = useMaintenanceTelemetry();
  const localZone = localTimeZone();
  const showUtc = localZone !== UTC_ZONE;
  const handleRetry = () => {
    trackRetry();
    setIsRetrying(true);
    window.setTimeout(() => setIsRetrying(false), 1600);
  };
  return <main className="flex min-h-[100dvh] w-full flex-col items-center justify-between bg-background px-5 py-10 sm:px-6 sm:py-12" data-id="element-596" data-mp="3bonnqo03da7l">
      <header className="flex w-full max-w-xl justify-center" data-id="element-597" data-mp="3glxkyptq2q5z">
        <img src="https://cdn.magicpatterns.com/uploads/7uSyNCSxyvrYGw6r5G7xpa/Turing_Logo_Full_Black.svg" alt="Turing" className="h-6 w-auto sm:h-7 dark:invert" data-id="element-598" data-mp="2izq86sn9lrij" />
      </header>

      <section className="flex w-full max-w-xl flex-col items-center py-12 text-center sm:py-16" data-id="element-599" data-mp="2swcr3bw168hx">
        <span className="flex items-center gap-2 text-center font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground sm:text-xs sm:tracking-[0.18em]" data-id="element-600" data-mp="2xomm36c05yr2">
          <Wrench className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-601" data-mp="2570gc7l21sdi" />
          503 · {maintenance.title}
        </span>

        <h1 className="mt-5 text-balance font-heading text-3xl font-semibold tracking-tight text-foreground sm:mt-6 sm:text-4xl md:text-5xl" data-id="element-602" data-mp="1x5sx6tmow860">
          We&rsquo;ll be back in a few hours.
        </h1>

        <p className="mt-4 text-pretty text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-603" data-mp="26vcevvyo1vza">
          {maintenance.summary}
        </p>

        <div className="mt-9 flex w-full flex-col items-center sm:mt-10" data-id="element-604" data-mp="21zx5phblso97">
          <p className="font-mono text-[2.25rem] leading-none tabular-nums tracking-tight text-foreground sm:text-5xl" aria-live="polite" role="timer" data-id="element-605" data-mp="1e89t84f39fqg">
            {formatRemaining(remaining)}
          </p>
          <p className="mt-3 max-w-[18rem] font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground sm:max-w-none sm:text-xs" data-id="element-606" data-mp="1j6jo0dwwiebj">
            {isOverdue ? 'Wrapping up — check back shortly' : 'Estimated time until we reopen'}
          </p>
          <p className="mt-4 text-pretty text-sm text-foreground" data-id="element-607" data-mp="2uocgfrnpckeq">
            Back online around{' '}
            <span className="font-medium" data-id="element-608" data-mp="gglcd5b7rbqf">{formatTime(endsAt, localZone)}</span>
            {showUtc && <span className="text-muted-foreground" data-id="element-609" data-mp="lb7eehvenrxu"> ({formatTime(endsAt, UTC_ZONE)})</span>}
          </p>
        </div>

        <div className="mt-9 flex w-full justify-center sm:mt-10" data-id="element-610" data-mp="cc3sl64usyfn">
          <Button size="lg" onClick={handleRetry} disabled={isRetrying} className="h-11 w-full max-w-xs sm:h-9 sm:w-auto" data-id="element-611" data-mp="102g935e603hu">
            <RefreshCw className={'h-4 w-4 ' + (isRetrying ? 'animate-spin' : '')} aria-hidden="true" data-id="element-612" data-mp="1465trjrpy0xl" />
            {isRetrying ? 'Checking…' : 'Check again'}
          </Button>
        </div>
      </section>

      <SiteFooter data-id="element-613" data-mp="1j12akldlfwma" />
    </main>;
}